import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import draftImg from '../../resources/images/draft_vid.png';
import Header from '../../Components/Header/Header';
import { useAuthContext } from '../../hooks/UseAuthContext';
import Loader from '../../Components/Loader/Loader';
import api from '../../api';
// import './completedvids.css';

const VIDEOS_PER_PAGE = 12;

function latestTime(video) {
  if (video?.created_at) {
    const t = new Date(video.created_at).getTime();
    if (!Number.isNaN(t) && t > 0) return t;
  }
  if (typeof video?._id === 'string' && /^[a-f0-9]{24}$/i.test(video._id)) {
    return parseInt(video._id.slice(0, 8), 16) * 1000;
  }
  return 0;
}

function CompletedDrafts() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const { user } = useAuthContext();

  // Fetch completed drafts for the signed-in user only
  useEffect(() => {
    if (!user?.token) {
      setLoading(false);
      return;
    }
    api.get('/video-drafts', { params: { mine: 1 } })
      .then(response => {
        const rows = Array.isArray(response.data) ? response.data : [];
        const completedOnly = rows.filter(video =>
          video.status === 'completed' ||
          video.status === 'ready' ||
          video.status === 'composed'
        );
        setVideos(completedOnly);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching completed drafts:', error);
        setLoading(false);
      });
  }, [user?.token]);

  const handleNavigate = (id, data) => {
    navigate(`/compose/${id}`, { state: { videoUrl: data } });
  };

  const filteredVideos = useMemo(() => {
    return [...videos].sort((a, b) => latestTime(b) - latestTime(a));
  }, [videos]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredVideos.length / VIDEOS_PER_PAGE);
  const startIndex = (currentPage - 1) * VIDEOS_PER_PAGE;
  const endIndex = startIndex + VIDEOS_PER_PAGE;
  const currentVideos = filteredVideos.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="completed-vids-container">
      <Header />

      <div className="completed-vids">
        {/* Header with Filter */}
        <div className="complete-vid-header">
          <h2>✅ Completed Drafts</h2>
        </div>

        {/* Video Grid */}
        {currentVideos.length > 0 ? (
          <>
            <div className="videos">
              {currentVideos.map((video) => (
                <div
                  key={video._id}
                  className="video-element"
                  onClick={() => handleNavigate(video._id, video)}
                >
                  <img src={draftImg} alt="Completed draft thumbnail" />
                  <h3>{video.title || 'Untitled Draft'}</h3>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <div className="pagination-info">
                  Showing {startIndex + 1}–{Math.min(endIndex, filteredVideos.length)} of {filteredVideos.length}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  ← Prev
                </button>

                {getPageNumbers().map((page, index) =>
                  page === '...' ? (
                    <span key={`dots-${index}`} className="page-dots">
                      ⋯
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={currentPage === page ? 'active' : ''}
                      aria-label={`Page ${page}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <p>No completed drafts found.</p>
          </div>
        )}
      </div>

      {loading && <Loader />}
    </div>
  );
}

export default CompletedDrafts;